// Movie edit diffing — the engine behind version compare + cherry-pick.
// Pure functions over two edit states: live movie docs AND editVersions
// snapshots share the same shape, so both diff interchangeably. Output is
// structured and clip-ID-keyed so the UI can render a readable change list
// and offer per-clip pulls.

// Deep equality via key-sorted JSON (clip objects are small, plain data).
function stable(v) {
  if (v === undefined) return 'u';
  return JSON.stringify(v, (_k, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val).sort().reduce((o, k) => { o[k] = val[k]; return o; }, {});
    }
    return val;
  });
}
const eq = (a, b) => stable(a) === stable(b);

const fmtSec = (s) => (Number.isFinite(Number(s)) ? Number(s).toFixed(2) : '—');
const fmtRange = (c) => `${fmtSec(c?.inSec)}–${fmtSec(c?.outSec)}s`;
const fmtFade = (f) => (f && Number(f.sec) >= 0.05 ? `${Number(f.sec).toFixed(1)}s ${f.color === 'white' ? 'white' : 'black'}` : 'none');

// Which top-level keys of two objects differ (for grade/output summaries).
function changedKeys(a = {}, b = {}) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  return [...keys].filter((k) => !eq((a || {})[k], (b || {})[k])).sort();
}

// Per-clip facets: name, the fields it covers, and a human summary builder.
// Facet keys drive the cherry-pick UI labels; `fields` drive the comparison.
const CLIP_FACETS = [
  {
    key: 'source', label: 'Source',
    fields: ['assetId', 'tripId', 'url', 's3Key', 'sourceDurationSec'],
    summary: () => 'different source media',
  },
  {
    key: 'trim', label: 'Trim',
    fields: ['inSec', 'outSec'],
    summary: (a, b) => `${fmtRange(a)} → ${fmtRange(b)}`,
  },
  {
    key: 'retime', label: 'Speed',
    fields: ['speed', 'speedKeys'],
    summary: (a, b) => {
      const n = (c) => (Array.isArray(c?.speedKeys) && c.speedKeys.length
        ? `${c.speedKeys.length} ramp keys`
        : `${Number(c?.speed) > 0 ? Number(c.speed).toFixed(2) : '1.00'}x flat`);
      return `${n(a)} → ${n(b)}`;
    },
  },
  {
    key: 'dissolve', label: 'Dissolve',
    fields: ['dissolveSec'],
    summary: (a, b) => `${fmtSec(a?.dissolveSec || 0)}s → ${fmtSec(b?.dissolveSec || 0)}s`,
  },
  {
    key: 'fades', label: 'Fades',
    fields: ['fadeIn', 'fadeOut'],
    summary: (a, b) => `in ${fmtFade(a?.fadeIn)}→${fmtFade(b?.fadeIn)} · out ${fmtFade(a?.fadeOut)}→${fmtFade(b?.fadeOut)}`,
  },
  {
    key: 'reframe', label: 'Reframe',
    fields: ['reframe'],
    summary: (a, b) => {
      const n = (c) => {
        const r = c?.reframe;
        if (!r) return 'none';
        const wp = Array.isArray(r.waypoints) ? r.waypoints.length : 0;
        return `${r.cropMode || 'auto'}, ${wp} waypoints${r.subjectTrackId ? ', tracked' : ''}`;
      };
      return `${n(a)} → ${n(b)}`;
    },
  },
  {
    key: 'grade', label: 'Color',
    fields: ['grade', 'gradeLocked'],
    summary: (a, b) => {
      const ks = changedKeys(a?.grade, b?.grade);
      if (!ks.length) return 'grade provenance changed';
      return `changed: ${ks.slice(0, 6).join(', ')}${ks.length > 6 ? ` +${ks.length - 6}` : ''}`;
    },
  },
  {
    key: 'audio', label: 'Audio',
    fields: ['volume', 'mute'],
    summary: (a, b) => `volume ${a?.volume ?? 1}→${b?.volume ?? 1}${(a?.mute || b?.mute) ? `, mute ${!!a?.mute}→${!!b?.mute}` : ''}`,
  },
  {
    key: 'label', label: 'Label',
    fields: ['label'],
    summary: (a, b) => `“${a?.label || ''}” → “${b?.label || ''}”`,
  },
];

// Compare one clip across two edits → array of changed facets (empty = same).
export function diffClip(a, b) {
  const facets = [];
  for (const f of CLIP_FACETS) {
    const changed = f.fields.some((k) => !eq(a?.[k], b?.[k]));
    if (changed) facets.push({ key: f.key, label: f.label, summary: f.summary(a, b) });
  }
  return facets;
}

const songSummary = (s) => `${s.label || s.source || 'song'} @${fmtSec(s.movieOffset || 0)}s`;

// Diff two full edit states (movie docs or editVersion snapshots).
// Returns { clips: {added, removed, changed, reordered}, sections: [...] }.
export function diffEdits(a, b) {
  const ca = Array.isArray(a?.clips) ? a.clips : [];
  const cb = Array.isArray(b?.clips) ? b.clips : [];
  const byIdA = new Map(ca.map((c) => [c.id, c]));
  const byIdB = new Map(cb.map((c) => [c.id, c]));

  const added = cb.filter((c) => !byIdA.has(c.id));
  const removed = ca.filter((c) => !byIdB.has(c.id));
  const common = ca.filter((c) => byIdB.has(c.id)).map((c) => c.id);
  const changed = common
    .map((id) => {
      const facets = diffClip(byIdA.get(id), byIdB.get(id));
      return facets.length
        ? { id, label: byIdB.get(id).label || byIdA.get(id).label || id, facets }
        : null;
    })
    .filter(Boolean);
  const orderA = common.join('|');
  const orderB = cb.filter((c) => byIdA.has(c.id)).map((c) => c.id).join('|');
  const reordered = orderA !== orderB;

  // Movie-level sections (each is adoptable as a unit in the cherry-pick UI).
  const sections = [];
  const addSection = (key, label, va, vb, summary) => {
    if (!eq(va, vb)) sections.push({ key, label, summary });
  };
  addSection('songs', 'Soundtrack', a?.songs, b?.songs, (() => {
    const la = (a?.songs || []).map(songSummary).join(', ') || 'none';
    const lb = (b?.songs || []).map(songSummary).join(', ') || 'none';
    return `${la} → ${lb}`;
  })());
  addSection('viewerSongs', 'Viewer songs', { s: a?.viewerSongs, d: a?.viewerSongDefault }, { s: b?.viewerSongs, d: b?.viewerSongDefault },
    `${(a?.viewerSongs || []).length} → ${(b?.viewerSongs || []).length} tracks`);
  addSection('grade', 'Movie grade', a?.grade, b?.grade, (() => {
    const ks = changedKeys(a?.grade, b?.grade);
    return ks.length ? `changed: ${ks.slice(0, 6).join(', ')}${ks.length > 6 ? ` +${ks.length - 6}` : ''}` : 'changed';
  })());
  addSection('fade', 'Intro/outro fade', { i: a?.intro, o: a?.outro }, { i: b?.intro, o: b?.outro },
    `intro ${fmtSec(a?.intro || 0)}→${fmtSec(b?.intro || 0)}s · outro ${fmtSec(a?.outro || 0)}→${fmtSec(b?.outro || 0)}s`);
  addSection('output', 'Output settings', a?.output, b?.output, (() => {
    const ks = changedKeys(a?.output, b?.output);
    return ks.length ? `changed: ${ks.join(', ')}` : 'changed';
  })());
  addSection('defaultAudio', 'Default audio', a?.defaultAudio, b?.defaultAudio,
    `${a?.defaultAudio || 'original'} → ${b?.defaultAudio || 'original'}`);

  const identical = !added.length && !removed.length && !changed.length && !reordered && !sections.length;
  return { clips: { added, removed, changed, reordered }, sections, identical };
}
